import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Users, 
  BookOpen,
  Calendar,
  User,
  CheckCircle,
  ArrowLeft,
  Save,
  X,
  UserPlus,
  AlertCircle
} from 'lucide-react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useStudents } from '../students/StudentProvider';
import { useClasses } from '../classes/ClassesContext';
import { useEnrollments } from './EnrollmentsProvider';
import enrollmentsService from '../../services/enrollmentsService';
import classSessionsService from '../../services/classSessionsService';

const BulkEditEnrollments = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { students, loading: studentsLoading, fetchStudents } = useStudents();
  const { classes, loading: classesLoading, fetchClasses } = useClasses();
  const { enrollments, bulkUpdateEnrollments, loading: enrollmentLoading, fetchEnrollments } = useEnrollments();
  
  const [selectedEnrollments, setSelectedEnrollments] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [notes, setNotes] = useState('');
  const [notification, setNotification] = useState({ type: '', message: '', show: false });
  const [classSessions, setClassSessions] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const loadedClassesRef = useRef(new Set());

  // Get enrollment IDs from URL params
  const enrollmentIds = searchParams.get('ids')?.split(',') || [];

  // Load data on component mount
  useEffect(() => {
    fetchStudents();
    fetchClasses();
    fetchEnrollments();
  }, []);

  // Load sessions for a class
  const loadSessionsForClass = useCallback(async (classId) => {
    // Check if already loaded
    if (loadedClassesRef.current.has(classId)) return;
    
    // Mark as loading to prevent duplicate requests
    loadedClassesRef.current.add(classId);
    
    try {
      const result = await classSessionsService.getSessionsForClass(classId);
      
      if (result.success) {
        setClassSessions(prev => ({
          ...prev,
          [classId]: result.data || []
        }));
      } else {
        console.error('Failed to load sessions for class:', classId, result.error);
        setClassSessions(prev => ({
          ...prev,
          [classId]: []
        }));
      }
    } catch (error) {
      console.error('Failed to load sessions for class:', classId, error);
      setClassSessions(prev => ({
        ...prev,
        [classId]: []
      }));
    }
  }, []); // No dependencies to avoid infinite loops

  // Find selected enrollments when data is loaded
  useEffect(() => {
    if (enrollments.length > 0 && enrollmentIds.length > 0) {
      const foundEnrollments = enrollments.filter(e => 
        enrollmentIds.includes(e.id.toString()) || enrollmentIds.includes(e.id)
      );
      setSelectedEnrollments(foundEnrollments);
    }
  }, [enrollments.length, enrollmentIds.join(',')]); // Use stable dependencies

  // Load sessions for all classes when classes are loaded
  useEffect(() => {
    if (classes.length > 0) {
      classes.forEach(classItem => {
        loadSessionsForClass(classItem.id);
      });
    }
  }, [classes.length, loadSessionsForClass]); // Include loadSessionsForClass in dependencies

  // Format session information for display
  const formatSessionInfo = (classId) => {
    const sessions = classSessions[classId];
    
    if (!sessions) {
      return 'Loading...';
    }
    
    if (sessions.length === 0) {
      return 'No sessions scheduled';
    }

    // Sort sessions by date
    const sortedSessions = [...sessions].sort((a, b) => new Date(a.session_date) - new Date(b.session_date));
    const firstSession = sortedSessions[0];
    const sessionCount = sessions.length;
    
    if (sessionCount === 1) {
      const firstDate = new Date(firstSession.session_date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
      return firstDate;
    }

    const firstDate = new Date(firstSession.session_date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
    return `${sessionCount} sessions, starts ${firstDate}`;
  };

  const getStudentDisplayName = (student) => {
    const firstName = student.studentFirstName || student.student_first_name || student.firstName || '';
    const lastName = student.studentLastName || student.student_last_name || student.lastName || '';
    return `${firstName} ${lastName}`.trim();
  };

  const getGenderPlaceholderStyle = (gender) => {
    switch (gender?.toLowerCase()) {
      case 'female':
        return 'bg-gradient-to-br from-pink-100 to-pink-200';
      case 'male':
        return 'bg-gradient-to-br from-blue-100 to-blue-200';
      default:
        return 'bg-gradient-to-br from-[#7A99D8] to-[#6B8BC9]';
    }
  };

  const showNotification = (type, message) => {
    setNotification({ type, message, show: true });
    setTimeout(() => {
      setNotification({ type: '', message: '', show: false });
    }, 5000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedClass) {
      showNotification('error', 'Please select a class to transfer enrollments to.');
      return;
    }

    if (selectedEnrollments.length === 0) {
      showNotification('error', 'No enrollments to update.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Bulk update all enrollments
      const enrollmentIds = selectedEnrollments.map(e => e.id);
      await bulkUpdateEnrollments(enrollmentIds, {
        classId: selectedClass.id,
        notes: notes
      });
      
      showNotification('success', `Successfully updated ${selectedEnrollments.length} enrollment(s)!`);
      
      // Navigate back to enrollments list after a short delay
      setTimeout(() => {
        navigate('/enrollments');
      }, 2000);
    } catch (error) {
      console.error('Error updating enrollments:', error);
      showNotification('error', 'Failed to update enrollments. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (studentsLoading || classesLoading || enrollmentLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-gray-200 dark:border-gray-700 border-t-[#7A99D8] rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading enrollment details...</p>
        </div>
      </div>
    );
  }

  if (selectedEnrollments.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Users className="w-16 h-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-[#3C3C3C] dark:text-gray-100 mb-2">No Enrollments Found</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">The selected enrollments could not be found.</p>
          <Link
            to="/enrollments"
            className="bg-[#FFECC5] hover:bg-[#FFE4A3] dark:bg-[#FFD199] dark:hover:bg-[#FFCC80] text-[#3C3C3C] px-6 py-3 rounded-lg inline-flex items-center gap-2 transition-colors font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Enrollments
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Users className="w-8 h-8 text-[#7A99D8]" />
            <div>
              <h1 className="text-lg font-bold text-[#3C3C3C] dark:text-gray-100">Bulk Edit Enrollments</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">Transfer {selectedEnrollments.length} students to a different class</p>
            </div>
          </div>
          <Link
            to="/enrollments"
            className="bg-[#F5F5F5] hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-[#3C3C3C] dark:text-gray-100 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Enrollments
          </Link>
        </div>
      </div>

      {/* Notification */}
      {notification.show && (
        <div className={`mx-6 mt-6 p-4 rounded-lg border ${
          notification.type === 'success' 
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200' 
            : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200'
        }`}>
          <div className="flex items-center gap-3">
            {notification.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <span className="font-medium">{notification.message}</span>
          </div>
        </div>
      )}

      <div className="px-6 py-6">
        <form onSubmit={handleSubmit} className="max-w-6xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-8">
            
            {/* Selected Students Section */}
            <div>
              <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-100 mb-4">Selected Students ({selectedEnrollments.length})</h3>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {selectedEnrollments.map(enrollment => {
                  const student = students.find(s => s.id === (enrollment.studentId || enrollment.student_id));
                  const currentClass = classes.find(c => c.id === (enrollment.classId || enrollment.class_id));
                  
                  return (
                    <div key={enrollment.id} className="bg-[#F5F5F5] dark:bg-gray-700 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        {/* Student Photo */}
                        <div className="flex-shrink-0">
                          {student?.photoUrl || student?.photo_url ? (
                            <img
                              src={student.photoUrl || student.photo_url}
                              alt={getStudentDisplayName(student || enrollment)}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                          ) : (
                            <div className={`w-12 h-12 ${getGenderPlaceholderStyle(student?.gender || enrollment.studentGender)} rounded-full flex items-center justify-center`}>
                              <User className="w-6 h-6 text-white" />
                            </div>
                          )}
                        </div>
                        
                        {/* Student Info */}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-[#3C3C3C] dark:text-gray-100 truncate">
                            {getStudentDisplayName(student || enrollment)}
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                            Current: {currentClass?.title || enrollment.className || enrollment.class_name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-500">
                            Age: {student?.age || enrollment.studentAge || enrollment.student_age} • 
                            Level: {student?.experience || 'Not specified'}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Select New Class Section */}
            <div>
              <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-100 mb-4">Transfer All Students To</h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
                  Select Destination Class
                </label>
                <select
                  value={selectedClass?.id || ''}
                  onChange={(e) => {
                    const classId = e.target.value;
                    const classItem = classes.find(c => c.id === classId);
                    setSelectedClass(classItem || null);
                  }}
                  className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent text-sm"
                  required
                >
                  <option value="">Choose a class...</option>
                  {classes
                    .filter(classItem => {
                      // Don't show classes that students are already enrolled in
                      const currentClassIds = selectedEnrollments.map(e => e.classId || e.class_id);
                      return !currentClassIds.includes(classItem.id);
                    })
                    .map(classItem => {
                      const dateStr = formatSessionInfo(classItem.id);
                      
                      return (
                        <option key={classItem.id} value={classItem.id}>
                          {classItem.title} - ${classItem.price} • {dateStr} • Age {classItem.minimumAge || 'Any'}+ • {classItem.skillLevel || 'All Levels'}
                        </option>
                      );
                    })}
                </select>
              </div>

              {/* Selected Class Preview */}
              {selectedClass && (
                <div className="bg-[#FFECC5] dark:bg-[#FFD199] border border-[#FFE4A3] dark:border-[#FFCC80] rounded-lg p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#FFE4A3] dark:bg-[#FFCC80] rounded-lg flex items-center justify-center">
                      <BookOpen className="w-6 h-6 text-[#3C3C3C]" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-[#3C3C3C]">{selectedClass.title}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Price: ${selectedClass.price} • 
                        Min Age: {selectedClass.minimumAge || 'Any'}+ • 
                        Level: {selectedClass.skillLevel || 'All Levels'}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Instructor: {selectedClass.instructor} • 
                        Duration: {selectedClass.duration} hours
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Dates: {formatSessionInfo(selectedClass.id)}
                      </p>
                    </div>
                    <CheckCircle className="w-6 h-6 text-[#3C3C3C]" />
                  </div>
                </div>
              )}
            </div>

            {/* Additional Notes */}
            <div>
              <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-100 mb-4">Additional Notes</h3>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent"
                placeholder="Add any additional notes about this bulk enrollment transfer..."
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
              <Link
                to="/enrollments"
                className="bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-[#3C3C3C] dark:text-gray-100 px-6 py-2 rounded-lg transition-colors font-medium flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Cancel
              </Link>
              <button
                type="submit"
                disabled={isSubmitting || !selectedClass}
                className="bg-[#FFECC5] hover:bg-[#FFE4A3] dark:bg-[#FFD199] dark:hover:bg-[#FFCC80] disabled:bg-gray-200 dark:disabled:bg-gray-700 disabled:text-gray-500 dark:disabled:text-gray-500 text-[#3C3C3C] px-6 py-2 rounded-lg transition-colors font-medium flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {isSubmitting ? 'Updating...' : `Update ${selectedEnrollments.length} Enrollment${selectedEnrollments.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BulkEditEnrollments;